import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  MenuItem,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  Work,
  People,
  CurrencyRupee,
  Visibility,
} from "@mui/icons-material";
import apiEndpoints from "../../apiconfig";
import "../dashboard/dashboard.css";

const Analysis = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [timeRange, setTimeRange] = useState("30days");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const timeRangeOptions = [
    { value: "7days", label: "Last 7 days" },
    { value: "30days", label: "Last 30 days" },
    { value: "90days", label: "Last 3 months" },
    { value: "180days", label: "Last 6 months" },
    { value: "1year", label: "Last year" },
  ];

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("token");
        const response = await fetch(
          `${apiEndpoints.analysis}?timeRange=${timeRange}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch analytics data");
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || "Error fetching data");
        }

        setAnalyticsData(data.data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching analytics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [timeRange]);

  const processStatsData = () => {
    if (!analyticsData) return [];

    return [
      {
        title: "Total Jobs Posted",
        value: analyticsData.total_jobs_posted.toLocaleString(),
        change: "+0%",
        trend: "up",
        icon: <Work />,
        color: "#2563eb",
        gradient: "linear-gradient(135deg, #e0ecff 0%, #dbeafe 100%)",
      },
      {
        title: "Total Applications",
        value: analyticsData.total_applications.toLocaleString(),
        change: "+0%",
        trend: "up",
        icon: <People />,
        color: "#7c3aed",
        gradient: "linear-gradient(135deg, #f0e5ff 0%, #e9d5ff 100%)",
      },
      {
        title: "Total Spent",
        value: `\u20B9${analyticsData.total_spent.toLocaleString()}`,
        change: "+0%",
        trend: "up",
        icon: <CurrencyRupee />,
        color: "#d97706",
        gradient: "linear-gradient(135deg, #fff4d6 0%, #fef3c7 100%)",
      },
      {
        title: "Active Users",
        value: analyticsData.total_Activity_User.toLocaleString(),
        change: "+0%",
        trend: "up",
        icon: <Visibility />,
        color: "#dc2626",
        gradient: "linear-gradient(135deg, #ffe9df 0%, #fee2e2 100%)",
      },
    ];
  };

  const processJobsOverTimeData = () => {
    if (!analyticsData?.jobs_applications) return [];

    return analyticsData.jobs_applications.map((item) => ({
      month: item.month.split("-")[1],
      jobs: item.jobs_posted,
      applications: item.applications_received,
    }));
  };

  const processCategoryData = () => {
    if (!Array.isArray(analyticsData?.job_categories)) return [];

    const colors = ["#2563eb", "#f59e0b", "#7c3aed", "#ef4444", "#06b6d4"];

    return analyticsData.job_categories
      .filter((category) => category?.count > 0)
      .map((category, index) => ({
        name: category?.category || "Unknown",
        value: Number(category?.count) || 0,
        color: colors[index % colors.length],
      }));
  };

  const processTopJobsData = () => {
    if (!analyticsData?.top_jobs) return [];

    return analyticsData.top_jobs.map((job) => {
      const hasCompletedBid = String(job.Status || "")
        .toLowerCase()
        .includes("completed");
      const isActive =
        (job.isActive === "1" || job.isActive === 1) && !hasCompletedBid;
      const status = isActive ? "Active" : "Closed";

      return {
        id: Math.random().toString(36).substr(2, 9),
        title: job["Job Title"] || "Untitled Job",
        applications: parseInt(job.Applications) || 0,
        views: 0,
        budget: job.Budget ? `\u20B9${parseFloat(job.Budget).toFixed(2)}` : "\u20B90.00",
        status,
        conversionRate: "0%",
        isActive,
      };
    });
  };

  const jobsChartLegend = [
    { key: "applications", label: "Applications", color: "#93c5fd" },
    { key: "jobs", label: "Jobs", color: "#1d4ed8" },
  ];

  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const labelRadius = outerRadius + (isMobile ? 10 : isTablet ? 12 : 16);
    const rawX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const rawY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const minX = isMobile ? 28 : isTablet ? 24 : 20;
    const x = Math.max(rawX, minX);

    return (
      <text
        x={x}
        y={rawY}
        fill="#64748B"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        style={{
          fontSize: isMobile ? "10px" : isTablet ? "11px" : "12px",
          fontWeight: 600,
        }}
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  const StatCard = ({ stat }) => (
    <Box
      className="stat-card"
      sx={{
        backgroundColor: alpha(stat.color, 0.14),
        minHeight: { xs: 96, sm: 102, md: 108 },
        p: { xs: "10px 12px", sm: "12px 14px", md: "12px 16px" },
        gap: { xs: "10px", md: "12px" },
        borderRadius: { xs: "14px", md: "18px" },
      }}
    >
      <Box
        className="stat-icon"
        sx={{
          color: stat.color,
          boxShadow: "none",
          width: { xs: 34, sm: 36, md: 40 },
          height: { xs: 34, sm: 36, md: 40 },
          borderRadius: { xs: "9px", md: "10px" },
        }}
      >
        {React.cloneElement(stat.icon, {
          sx: { fontSize: { xs: "18px", sm: "20px", md: "22px" } },
        })}
      </Box>
      <Box className="stat-text">
        <Typography
          className="card-value"
          sx={{
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: { xs: "1.5rem", sm: "1.65rem", md: "1.9rem" },
            lineHeight: 1,
            color: "#1E2A44",
          }}
        >
          {stat.value}
        </Typography>
        <Typography
          className="card-title"
          sx={{
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            color: "#4A5C78",
            fontSize: { xs: "12px", sm: "13px", md: "14px" },
            lineHeight: 1.25,
          }}
        >
          {stat.title}
        </Typography>
      </Box>
    </Box>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            padding: "12px",
            border: "1px solid #dbe2ee",
            borderRadius: "12px",
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
            fontSize: isMobile ? "12px" : "14px",
          }}
        >
          <Typography sx={{ m: 0, fontWeight: 600, color: "#1f2937" }}>{label}</Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              sx={{
                mt: 0.5,
                color: entry.color,
                fontSize: isMobile ? "12px" : "13px",
              }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!analyticsData) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        padding: {
          xs: "14px 12px 20px",
          sm: "16px",
          md: "20px",
          lg: "24px",
        },
        boxSizing: "border-box",
        background:
          "radial-gradient(circle at 0% 0%, rgba(219, 234, 254, 0.6) 0%, rgba(248, 250, 252, 0) 42%), radial-gradient(circle at 100% 0%, rgba(233, 213, 255, 0.5) 0%, rgba(248, 250, 252, 0) 40%)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isTablet ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isTablet ? "flex-start" : "center",
          gap: isTablet ? 2 : 0,
          mb: 3,
          p: isTablet ? 2 : 2.5,
          borderRadius: "20px",
          border: "1px solid #e2e8f0",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
        }}
      >
        <Box>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 700,
              color: "#1f2937",
              marginBottom: "8px",
            }}
          >
            Analytics
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#64748b",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            Track your job posting performance and insights
          </Typography>
        </Box>

        <TextField
          select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          size="small"
          sx={{
            minWidth: isTablet ? "100%" : "220px",
            mt: isTablet ? 1 : 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "#ffffff",
              "& fieldset": {
                borderColor: "#d7e0ea",
              },
              "&:hover fieldset": {
                borderColor: "#9db6d1",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2563eb",
              },
            },
          }}
        >
          {timeRangeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: { xs: 1.5, sm: 2, md: 2.25 },
          width: "100%",
          marginBottom: "32px",
        }}
      >
        {processStatsData().map((stat, index) => (
          <Box key={index}>
            <StatCard stat={stat} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isTablet ? "column" : "row",
          gap: 3,
          width: "100%",
          marginBottom: "24px",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            sx={{
              p: isTablet ? 2 : 3,
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              minHeight: isMobile ? "300px" : isTablet ? "360px" : "500px",
              overflow: "hidden",
              boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                marginBottom: "20px",
                fontSize: isMobile ? "1rem" : "1.25rem",
              }}
            >
              Job Categories
            </Typography>
            {processCategoryData().length > 0 ? (
              <>
                <Box
                  sx={{
                    width: "100%",
                    height: isMobile ? "240px" : isTablet ? "280px" : "320px",
                    overflow: "visible",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={processCategoryData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 28 : isTablet ? 34 : 42}
                        outerRadius={isMobile ? 72 : isTablet ? 92 : 120}
                        paddingAngle={2}
                        dataKey="value"
                        stroke="#ffffff"
                        strokeWidth={2}
                        labelLine={!isMobile}
                        label={isMobile ? false : renderPieLabel}
                      >
                        {processCategoryData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box
                  mt={1.5}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    columnGap: 1.5,
                    rowGap: 1,
                    width: "100%",
                  }}
                >
                  {processCategoryData().map((category, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="flex-start"
                      gap="6px"
                      sx={{ minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: category.color,
                          mt: "4px",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#475569",
                          fontWeight: 600,
                          whiteSpace: "normal",
                          wordBreak: "normal",
                          overflowWrap: "break-word",
                          lineHeight: 1.35,
                        }}
                      >
                        {category.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={isMobile ? "250px" : "360px"}
                sx={{ color: "#64748b" }}
              >
                <Typography variant="body1">No category data available</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ flex: 1.5, minWidth: 0 }}>
          <Paper
            sx={{
              p: isTablet ? 2 : 3,
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              minHeight: isMobile ? "300px" : isTablet ? "380px" : "500px",
              overflow: "hidden",
              boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                marginBottom: "2px",
                fontSize: isMobile ? "1rem" : "1.25rem",
              }}
            >
              Jobs & Applications Over Time
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5 }}>
              (+43%) than last period
            </Typography>
            {isMobile && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  columnGap: 1.2,
                  rowGap: 0.6,
                  mb: 1.2,
                }}
              >
                {jobsChartLegend.map((item) => (
                  <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: item.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: item.color,
                        fontWeight: 600,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "break-word",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {processJobsOverTimeData().length > 0 ? (
              <Box sx={{ width: "100%", height: isMobile ? "260px" : isTablet ? "320px" : "380px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={processJobsOverTimeData()}
                    barCategoryGap={isMobile ? "22%" : "28%"}
                    margin={{
                      top: 8,
                      right: isMobile ? 0 : 10,
                      left: isMobile ? 0 : isTablet ? -8 : -6,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid strokeDasharray="2 6" vertical={false} stroke="#e2e8f0" />
                    <XAxis
                      dataKey="month"
                      stroke="#94a3b8"
                      tickLine={false}
                      axisLine={false}
                      minTickGap={isMobile ? 10 : 14}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tickLine={false}
                      axisLine={false}
                      width={isMobile ? 24 : 34}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {!isMobile && (
                      <Legend
                        verticalAlign="top"
                        align="right"
                        iconType="circle"
                        wrapperStyle={{
                          fontSize: isMobile ? "11px" : "12px",
                          paddingBottom: "12px",
                        }}
                      />
                    )}
                    <Bar
                      dataKey="jobs"
                      name="Jobs"
                      fill="#1d4ed8"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={isMobile ? 18 : 28}
                    />
                    <Bar
                      dataKey="applications"
                      name="Applications"
                      fill="#93c5fd"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={isMobile ? 18 : 28}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={isMobile ? "250px" : "380px"}
                sx={{ color: "#64748b" }}
              >
                <Typography variant="body1">No data available for the selected time period</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Paper
          sx={{
            p: isMobile ? 2 : 3,
            borderRadius: "20px",
            border: "1px solid #e2e8f0",
            overflowX: "auto",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#1f2937",
              marginBottom: "20px",
              fontSize: isMobile ? "1rem" : "1.25rem",
            }}
          >
            Top Performing Jobs
          </Typography>
          {isMobile ? (
            <Box display="grid" gap={1.2}>
              {processTopJobsData().map((job) => (
                <Box
                  key={job.id}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "12px",
                    p: 1.4,
                    backgroundColor: "#FFFFFF",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "#1f2937",
                      mb: 0.8,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {job.title}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" gap={1} mb={0.8}>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Applications: {job.applications}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      {job.budget}
                    </Typography>
                  </Box>
                  <Chip
                    label={job.isActive ? "Active" : "Closed"}
                    size="small"
                    sx={{
                      backgroundColor: job.isActive ? "#ecfdf5" : "#fee2e2",
                      color: job.isActive ? "#059669" : "#dc2626",
                      border: `1px solid ${job.isActive ? "#bbf7d0" : "#fecaca"}`,
                      fontWeight: 600,
                      fontSize: "11px",
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size={isTablet ? "small" : "medium"}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        padding: isTablet ? "10px 12px" : "16px",
                        minWidth: "150px",
                      }}
                    >
                      Job Title
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        padding: isTablet ? "10px 12px" : "16px",
                      }}
                    >
                      Applications
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        padding: isTablet ? "10px 12px" : "16px",
                      }}
                    >
                      Budget
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        padding: isTablet ? "10px 12px" : "16px",
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processTopJobsData().map((job) => (
                    <TableRow
                      key={job.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8fbff",
                        },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ padding: isTablet ? "10px 12px" : "16px" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: "#1f2937",
                            fontSize: isTablet ? "12px" : "14px",
                          }}
                        >
                          {job.title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: isTablet ? "10px 12px" : "16px" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontSize: isTablet ? "12px" : "14px",
                          }}
                        >
                          {job.applications}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: isTablet ? "10px 12px" : "16px" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontSize: isTablet ? "12px" : "14px",
                          }}
                        >
                          {job.budget}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: isTablet ? "10px 12px" : "16px" }}>
                        <Chip
                          label={job.isActive ? "Active" : "Closed"}
                          size={isTablet ? "small" : "medium"}
                          sx={{
                            backgroundColor: job.isActive ? "#ecfdf5" : "#fee2e2",
                            color: job.isActive ? "#059669" : "#dc2626",
                            border: `1px solid ${job.isActive ? "#bbf7d0" : "#fecaca"}`,
                            fontWeight: 500,
                            fontSize: isTablet ? "12px" : "14px",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Analysis;
